import { SupabaseJob } from '@applicator/shared'

import { loadRelevanceSettings } from '../../../lib/relevanceSettings'

// Helper function to remove job from analyzing set
export function removeFromAnalyzing(
  jobId: string,
  setAnalyzingJobIds: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  setAnalyzingJobIds((prev) => {
    const next = new Set(prev)
    next.delete(jobId)
    return next
  })
}

// Helper function to check if settings exist
const hasRelevanceSettings = (settings: Awaited<ReturnType<typeof loadRelevanceSettings>> | null): boolean => {
  return !!settings
}

// Helper function to load and set relevance settings
const loadAndSetRelevanceSettings = async (
  setRelevanceSettings: React.Dispatch<React.SetStateAction<Awaited<ReturnType<typeof loadRelevanceSettings>> | null>>
): Promise<Awaited<ReturnType<typeof loadRelevanceSettings>>> => {
  const settings = await loadRelevanceSettings()
  setRelevanceSettings(settings)
  return settings
}

// Helper function to get relevance settings
export async function getRelevanceSettings(
  relevanceSettings: Awaited<ReturnType<typeof loadRelevanceSettings>> | null,
  setRelevanceSettings: React.Dispatch<React.SetStateAction<Awaited<ReturnType<typeof loadRelevanceSettings>> | null>>
): Promise<Awaited<ReturnType<typeof loadRelevanceSettings>> | null> {
  if (hasRelevanceSettings(relevanceSettings)) {
    return relevanceSettings
  }
  return await loadAndSetRelevanceSettings(setRelevanceSettings)
}

// Helper function to get OpenAI configuration
export function getOpenAIConfig() {
  return {
    OPENAI_API_KEY: import.meta.env.VITE_OPENAI_API_KEY,
    ASSISTANT_ID: import.meta.env.VITE_OPENAI_ASSISTANT_ID,
  }
}

// Helper function to check if description is valid
export function isDescriptionValid(description: string, minLength: number): boolean {
  return !!(description && description !== 'N/A' && description.length >= minLength)
}

// Helper function to check if job matches ID
export function isJobMatchingId(job: SupabaseJob, jobId: string): boolean {
  return job.id === jobId
}

// Helper function to build relevance score field
export function buildRelevanceScoreField(score: number): { relevance_score: number } {
  return { relevance_score: score }
}

// Helper function to build relevance verdict field
export function buildRelevanceVerdictField(verdict: string | null): { relevance_verdict: string | null } {
  return { relevance_verdict: verdict }
}

// Helper function to build relevance reasoning field
export function buildRelevanceReasoningField(reasoning: string | null): { relevance_reasoning: string | null } {
  return { relevance_reasoning: reasoning }
}

// Helper function to build is relevant field
export function buildIsRelevantField(isRelevant: boolean): { is_relevant: boolean } {
  return { is_relevant: isRelevant }
}

// Helper function to build updated job relevance fields
export function buildUpdatedJobRelevanceFields(
  score: number,
  verdict: string | null,
  reasoning: string | null,
  isRelevant: boolean
): Partial<SupabaseJob> {
  return {
    relevance_score: score,
    relevance_verdict: verdict,
    relevance_reasoning: reasoning,
    is_relevant: isRelevant
  }
}

// Helper function to update single supabase job
export function updateSingleSupabaseJob(
  job: SupabaseJob,
  jobId: string,
  score: number,
  verdict: string | null,
  reasoning: string | null,
  isRelevant: boolean
): SupabaseJob | null {
  if (!isJobMatchingId(job, jobId)) {
    return null
  }
  return {
    ...job,
    ...buildUpdatedJobRelevanceFields(score, verdict, reasoning, isRelevant),
  }
}

// Helper function to update single job or return original
export function updateJobOrReturnOriginal(
  job: SupabaseJob,
  jobId: string,
  score: number,
  verdict: string | null,
  reasoning: string | null,
  isRelevant: boolean
): SupabaseJob {
  const updatedJob = updateSingleSupabaseJob(job, jobId, score, verdict, reasoning, isRelevant)
  return updatedJob || job
}

// Helper function to map jobs with updated job
export function mapJobsWithUpdatedJob(
  prev: SupabaseJob[],
  jobId: string,
  score: number,
  verdict: string | null,
  reasoning: string | null,
  isRelevant: boolean
): SupabaseJob[] {
  return prev.map((job) => updateJobOrReturnOriginal(job, jobId, score, verdict, reasoning, isRelevant))
}

// Helper function to create headers
export function createHeaders(OPENAI_API_KEY: string): Record<string, string> {
  return {
    'Authorization': `Bearer ${OPENAI_API_KEY}`,
    'Content-Type': 'application/json',
    'OpenAI-Beta': 'assistants=v2'
  }
}

// Helper function to mark job as analyzing
export function markJobAsAnalyzing(
  jobId: string,
  setAnalyzingJobIds: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  setAnalyzingJobIds((prev) => {
    if (prev.has(jobId)) return prev
    const next = new Set(prev)
    next.add(jobId)
    return next
  })
}
